import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CardComp, ButtonComp, Loading } from "../../components";
import {
  useGetGuarantorDetailsQuery,
  useConfirmGuarantorMutation,
  useDeclineGuarantorMutation,
} from "../../store/admin/loan";
import { toast } from "react-hot-toast";
import { formatMoney } from "../../utils/currencyFormat";

export default function GuarantorConfirmationPage() {
  const { guarantorToken } = useParams();
  const navigate = useNavigate();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeclineModal, setShowDeclineModal] = useState(false);

  const {
    data: guarantorData,
    isLoading,
    isError,
    error,
  } = useGetGuarantorDetailsQuery(guarantorToken);

  const [confirmGuarantor, { isLoading: isConfirming }] =
    useConfirmGuarantorMutation();
  const [declineGuarantor, { isLoading: isDeclining }] =
    useDeclineGuarantorMutation();

  const guarantor = guarantorData?.guarantor;
  const loanRequest = guarantorData?.loan_request;

  const handleConfirm = async () => {
    try {
      const result = await confirmGuarantor(guarantorToken).unwrap();
      if (result.status) {
        toast.success(
          result.message || "Guarantor request confirmed successfully"
        );
        setShowConfirmModal(false);
        // Redirect after 2 seconds
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      }
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to confirm guarantor request"
      );
    }
  };

  const handleDecline = async () => {
    try {
      const result = await declineGuarantor(guarantorToken).unwrap();
      if (result.status) {
        toast.success(
          result.message || "Guarantor request declined successfully"
        );
        setShowDeclineModal(false);
        // Redirect after 2 seconds
        setTimeout(() => {
          window.location.href = "/";
        }, 2000);
      }
    } catch (error) {
      toast.error(
        error?.data?.message || "Failed to decline guarantor request"
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-vh-100 d-flex justify-content-center align-items-center">
        <Loading />
      </div>
    );
  }

  if (isError || !guarantor || !loanRequest) {
    return (
      <div className="min-vh-100 d-flex justify-content-center align-items-center">
        <CardComp cardClassName="p-5 text-center">
          <h3 className="text-danger mb-3">Error Loading Request</h3>
          <p className="text-muted">
            {error?.data?.message ||
              "Unable to load guarantor request details. Please check the link and try again."}
          </p>
          <ButtonComp
            btnText="Go to Home"
            btnClassName="mt-3 bg-primary1 text-white border-0"
            onClick={() => navigate("/")}
          />
        </CardComp>
      </div>
    );
  }

  // Check if already processed
  const isAlreadyResponded = guarantor.status === "CONFIRMED" || guarantor.status === "DECLINED";
  const isConfirmed = guarantor.status === "CONFIRMED";
  const isDeclined = guarantor.status === "DECLINED";

  return (
    <div className="min-vh-100 bg-light py-5">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            <CardComp cardClassName="p-4 p-lg-5">
              {/* Header */}
              <div
                className="text-center mb-4 p-4 rounded"
                style={{
                  background: isConfirmed
                    ? "linear-gradient(135deg, #19bc31 0%, #20b16a 100%)"
                    : isDeclined
                    ? "linear-gradient(135deg, #ff4343 0%, #d40000 100%)"
                    : "linear-gradient(135deg, #ff9500 0%, #da6900 100%)",
                }}
              >
                <h2 className="text-white mb-2 fw-bold">
                  {isConfirmed
                    ? "✓ Guarantor Request Confirmed"
                    : isDeclined
                    ? "✗ Guarantor Request Declined"
                    : "🤝 Guarantor Request"}
                </h2>
                <p className="text-white mb-0" style={{ opacity: 0.9 }}>
                  {isAlreadyResponded
                    ? "Your response has been recorded. View details below."
                    : "Review the loan details before responding"}
                </p>
              </div>

              {/* Response Status Badge (if already responded) */}
              {isAlreadyResponded && (
                <CardComp
                  cardClassName="mb-4"
                  style={{
                    background: isConfirmed
                      ? "linear-gradient(135deg, rgba(25, 188, 49, 0.1) 0%, rgba(32, 177, 106, 0.1) 100%)"
                      : "linear-gradient(135deg, rgba(255, 67, 67, 0.1) 0%, rgba(212, 0, 0, 0.1) 100%)",
                    border: `2px solid ${isConfirmed ? "#19bc31" : "#ff4343"}`,
                  }}
                >
                  <div className="p-4 text-center">
                    <div
                      className="d-inline-block rounded-circle mb-3"
                      style={{
                        width: "64px",
                        height: "64px",
                        backgroundColor: isConfirmed ? "#19bc31" : "#ff4343",
                        fontSize: "32px",
                        color: "#ffffff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {isConfirmed ? "✓" : "✗"}
                    </div>
                    <h4
                      className="mb-2 fw-bold"
                      style={{
                        color: isConfirmed ? "#19bc31" : "#ff4343",
                      }}
                    >
                      {isConfirmed
                        ? "You have confirmed this guarantor request"
                        : "You have declined this guarantor request"}
                    </h4>
                    <p className="text-muted mb-0">
                      {isConfirmed
                        ? "Thank you for your response. Your confirmation has been recorded."
                        : "Your decision has been recorded and the borrower has been notified."}
                    </p>
                  </div>
                </CardComp>
              )}

              {/* Greeting */}
              <div className="mb-4">
                <h3 className="text-black1 fw-bold mb-2">
                  Hello, {guarantor.name}!
                </h3>
                <p className="text-muted">
                  {isAlreadyResponded
                    ? "Below are the details of the loan application you responded to:"
                    : "You have been requested to serve as a guarantor for a loan application. Please carefully review the details below before making your decision."}
                </p>
              </div>

              {/* Loan Details Card */}
              <CardComp
                cardClassName="mb-4"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255, 149, 0, 0.05) 0%, rgba(218, 105, 0, 0.05) 100%)",
                  border: "2px solid #ff9500",
                }}
              >
                <div className="p-4">
                  <h5 className="text-black1 fw-bold mb-4 text-center">
                    Loan Application Details
                  </h5>

                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                      <div>
                        <small className="text-muted d-block mb-1 text-uppercase">
                          Applicant Name
                        </small>
                        <h6 className="mb-0 fw-bold text-black1">
                          {loanRequest.member?.name || "N/A"}
                        </h6>
                        {loanRequest.member?.member_no && (
                          <small className="text-muted">
                            Membership No: {loanRequest.member.member_no}
                          </small>
                        )}
                      </div>
                    </div>

                    <div className="mb-3 pb-3 border-bottom">
                      <small className="text-muted d-block mb-1 text-uppercase">
                        Loan Type
                      </small>
                      <h6 className="mb-0 fw-semibold text-black1">
                        {loanRequest.loan_type?.name || "N/A"}
                      </h6>
                    </div>

                    <div className="text-center">
                      <small className="text-muted d-block mb-2 text-uppercase">
                        Loan Amount
                      </small>
                      <h2
                        className="mb-0 fw-bold"
                        style={{ color: "#ff9500", fontSize: "36px" }}
                      >
                        {formatMoney(loanRequest.amount || 0)}
                      </h2>
                    </div>
                  </div>
                </div>
              </CardComp>

              {/* Important Warning - Only show if not already responded */}
              {!isAlreadyResponded && (
                <CardComp
                  cardClassName="mb-4"
                  style={{
                    background: "#fff3cd",
                    border: "2px solid #ffc107",
                  }}
                >
                  <div className="p-4">
                    <div className="d-flex align-items-start mb-3">
                      <div
                        className="rounded-circle bg-warning d-flex align-items-center justify-content-center me-3"
                        style={{ width: "40px", height: "40px", flexShrink: 0 }}
                      >
                        <span className="text-white fw-bold">⚠️</span>
                      </div>
                      <div>
                        <h5 className="text-black1 fw-bold mb-2">
                          Important: Guarantor Responsibilities
                        </h5>
                        <ul className="mb-0 ps-3" style={{ lineHeight: "1.8" }}>
                          <li className="mb-2">
                            <strong>
                              Only stand as guarantor for someone you know very
                              well and trust.
                            </strong>
                          </li>
                          <li className="mb-2">
                            <strong>You are legally liable</strong> for the full
                            loan repayment if the borrower defaults.
                          </li>
                          <li className="mb-2">
                            If the borrower fails to make payments,{" "}
                            <strong>
                              you will be required to repay the loan
                            </strong>{" "}
                            on their behalf.
                          </li>
                          <li className="mb-2">
                            This is a{" "}
                            <strong>serious financial commitment</strong> that
                            could affect your credit and finances.
                          </li>
                          <li>
                            Please ensure you understand the borrower's financial
                            situation and ability to repay before confirming.
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardComp>
              )}

              {/* Action Buttons - Only show if not already responded */}
              {!isAlreadyResponded && (
                <div className="text-center mt-4">
                  <p className="text-muted mb-4">
                    After reviewing the details above, please choose your
                    response:
                  </p>
                  <div className="d-flex flex-column flex-md-row gap-3 justify-content-center">
                    <ButtonComp
                      btnText={
                        isConfirming ? "Confirming..." : "✓ Confirm as Guarantor"
                      }
                      btnClassName="bg-success text-white border-0 px-5 py-3 fw-semibold"
                      onClick={() => setShowConfirmModal(true)}
                      disabled={isConfirming || isDeclining}
                      loading={isConfirming}
                    />
                    <ButtonComp
                      btnText={isDeclining ? "Declining..." : "✗ Decline Request"}
                      btnClassName="bg-white text-danger border-2 border-danger px-5 py-3 fw-semibold"
                      onClick={() => setShowDeclineModal(true)}
                      disabled={isConfirming || isDeclining}
                      loading={isDeclining}
                    />
                  </div>
                </div>
              )}

              {/* Go to Home Button - Only show if already responded */}
              {isAlreadyResponded && (
                <div className="text-center mt-4">
                  <ButtonComp
                    btnText="Go to Home"
                    btnClassName="bg-primary1 text-white border-0 px-5 py-3 fw-semibold"
                    onClick={() => navigate("/")}
                  />
                </div>
              )}
            </CardComp>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            zIndex: 1050,
          }}
          onClick={() => !isConfirming && setShowConfirmModal(false)}
        >
          <CardComp
            cardClassName="p-0"
            style={{ 
              maxWidth: "550px", 
              width: "90%",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Icon */}
            <div
              className="p-4 text-center"
              style={{
                background: "linear-gradient(135deg, #19bc31 0%, #20b16a 100%)",
              }}
            >
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{
                  width: "64px",
                  height: "64px",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  fontSize: "32px",
                }}
              >
                ✓
              </div>
              <h4 className="text-white fw-bold mb-0">Confirm as Guarantor</h4>
            </div>

            {/* Close Button */}
            <button
              type="button"
              className="position-absolute border-0 bg-transparent"
              style={{
                top: "15px",
                right: "15px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isConfirming ? "not-allowed" : "pointer",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "#ffffff",
                fontSize: "18px",
                zIndex: 1,
              }}
              onClick={() => !isConfirming && setShowConfirmModal(false)}
              disabled={isConfirming}
            >
              ×
            </button>

            {/* Content */}
            <div className="p-4">
              {/* Warning Box */}
              <div
                className="p-3 mb-4 rounded"
                style={{
                  backgroundColor: "#fff3cd",
                  border: "2px solid #ffc107",
                }}
              >
                <div className="d-flex align-items-start">
                  <span
                    className="me-3"
                    style={{ fontSize: "24px", flexShrink: 0 }}
                  >
                    ⚠️
                  </span>
                  <div>
                    <h6 className="fw-bold text-black1 mb-2">
                      Important: Financial Responsibility
                    </h6>
                    <p className="mb-0 text-black1" style={{ fontSize: "14px", lineHeight: "1.6" }}>
                      By confirming, you accept <strong>full legal and financial responsibility</strong> for this loan repayment if the borrower defaults. This is a serious commitment that could impact your finances.
                    </p>
                  </div>
                </div>
              </div>

              {/* Confirmation Message */}
              <p className="text-black1 mb-4" style={{ fontSize: "15px", lineHeight: "1.7" }}>
                Are you sure you want to proceed with confirming as a guarantor for this loan? Please ensure you:
              </p>

              <ul className="mb-4 ps-3" style={{ fontSize: "14px", lineHeight: "1.8" }}>
                <li className="mb-2">Know the borrower very well and trust them</li>
                <li className="mb-2">Understand their financial situation</li>
                <li className="mb-2">Are prepared to repay the loan if they default</li>
                <li>Have carefully considered all risks involved</li>
              </ul>

              {/* Action Buttons */}
              <div className="d-flex flex-column flex-sm-row gap-3 mt-4">
                <button
                  type="button"
                  className="btn flex-fill fw-semibold py-3"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isConfirming}
                  style={{
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    color: "#45464e",
                    border: "2px solid #e5e5e5",
                    fontSize: "15px",
                    transition: "all 0.3s ease",
                    minHeight: "48px",
                  }}
                  onMouseEnter={(e) => {
                    if (!isConfirming) {
                      e.target.style.backgroundColor = "#f7f7f7";
                      e.target.style.borderColor = "#d0d0d0";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#e5e5e5";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn flex-fill fw-semibold py-3 text-white border-0"
                  onClick={handleConfirm}
                  disabled={isConfirming}
                  style={{
                    borderRadius: "10px",
                    backgroundColor: isConfirming ? "#6fa86f" : "#19bc31",
                    color: "#ffffff",
                    fontSize: "15px",
                    transition: "all 0.3s ease",
                    minHeight: "48px",
                    boxShadow: isConfirming ? "none" : "0 4px 12px rgba(25, 188, 49, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isConfirming) {
                      e.target.style.backgroundColor = "#17a82a";
                      e.target.style.boxShadow = "0 6px 16px rgba(25, 188, 49, 0.4)";
                      e.target.style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isConfirming) {
                      e.target.style.backgroundColor = "#19bc31";
                      e.target.style.boxShadow = "0 4px 12px rgba(25, 188, 49, 0.3)";
                      e.target.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {isConfirming ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Confirming...
                    </>
                  ) : (
                    "Yes, I Confirm"
                  )}
                </button>
              </div>
            </div>
          </CardComp>
        </div>
      )}

      {/* Decline Modal */}
      {showDeclineModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
            zIndex: 1050,
          }}
          onClick={() => !isDeclining && setShowDeclineModal(false)}
        >
          <CardComp
            cardClassName="p-0"
            style={{ 
              maxWidth: "500px", 
              width: "90%",
              borderRadius: "12px",
              overflow: "hidden",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.2)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header with Icon */}
            <div
              className="p-4 text-center"
              style={{
                background: "linear-gradient(135deg, #ff4343 0%, #d40000 100%)",
              }}
            >
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                style={{
                  width: "64px",
                  height: "64px",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  fontSize: "32px",
                }}
              >
                ✗
              </div>
              <h4 className="text-white fw-bold mb-0">Decline Guarantor Request</h4>
            </div>

            {/* Close Button */}
            <button
              type="button"
              className="position-absolute border-0 bg-transparent"
              style={{
                top: "15px",
                right: "15px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isDeclining ? "not-allowed" : "pointer",
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                color: "#ffffff",
                fontSize: "18px",
                zIndex: 1,
              }}
              onClick={() => !isDeclining && setShowDeclineModal(false)}
              disabled={isDeclining}
            >
              ×
            </button>

            {/* Content */}
            <div className="p-4">
              <p className="text-black1 mb-4" style={{ fontSize: "15px", lineHeight: "1.7" }}>
                Are you sure you want to decline this guarantor request? This action cannot be undone, and the borrower will be notified of your decision.
              </p>

              {/* Action Buttons */}
              <div className="d-flex flex-column flex-sm-row gap-3 mt-4">
                <button
                  type="button"
                  className="btn flex-fill fw-semibold py-3"
                  onClick={() => setShowDeclineModal(false)}
                  disabled={isDeclining}
                  style={{
                    borderRadius: "10px",
                    backgroundColor: "#ffffff",
                    color: "#45464e",
                    border: "2px solid #e5e5e5",
                    fontSize: "15px",
                    transition: "all 0.3s ease",
                    minHeight: "48px",
                  }}
                  onMouseEnter={(e) => {
                    if (!isDeclining) {
                      e.target.style.backgroundColor = "#f7f7f7";
                      e.target.style.borderColor = "#d0d0d0";
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = "#ffffff";
                    e.target.style.borderColor = "#e5e5e5";
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn flex-fill fw-semibold py-3 text-white border-0"
                  onClick={handleDecline}
                  disabled={isDeclining}
                  style={{
                    borderRadius: "10px",
                    backgroundColor: isDeclining ? "#cc3333" : "#ff4343",
                    color: "#ffffff",
                    fontSize: "15px",
                    transition: "all 0.3s ease",
                    minHeight: "48px",
                    boxShadow: isDeclining ? "none" : "0 4px 12px rgba(255, 67, 67, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    if (!isDeclining) {
                      e.target.style.backgroundColor = "#e63939";
                      e.target.style.boxShadow = "0 6px 16px rgba(255, 67, 67, 0.4)";
                      e.target.style.transform = "translateY(-1px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDeclining) {
                      e.target.style.backgroundColor = "#ff4343";
                      e.target.style.boxShadow = "0 4px 12px rgba(255, 67, 67, 0.3)";
                      e.target.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {isDeclining ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Declining...
                    </>
                  ) : (
                    "Yes, Decline"
                  )}
                </button>
              </div>
            </div>
          </CardComp>
        </div>
      )}
    </div>
  );
}
